import { AnalyticsStepType, DateMacro, Program } from '@citruscamps/citrus-client'
import { faCalendar } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Interval } from 'luxon'
import React from 'react'
import { IDateMacroQuery } from '../../../interfaces/DateMacroQuery'
import { isDateMacro } from '../../../utils/date-macro'
import { filterStepsByDuration, getIntervalByDateMacro } from '../../../utils/date-macro'
import { formatDateMacro, formatDateRange } from '../../../utils/formatters'
import { Popover } from '../../Popover/Popover'
import { DateMacroRange } from './DateMacroRange'
import { usePopper } from '../../Popover/hooks/usePopper'

export const isAnalyticsStep = (value: any): value is AnalyticsStepType => {
  return new Array<AnalyticsStepType>('hourly', 'daily', 'weekly', 'monthly', 'yearly').includes(
    value,
  )
}

interface IProps {
  values: IDateMacroQuery
  interval: Interval
  program?: Program
  isLarge?: boolean
  onSelect: (value: IDateMacroQuery) => Promise<void> | void
}

export const DateSelectContainer = ({
  values,
  interval,
  program,
  isLarge = false,
  onSelect: handleSelect,
}: IProps) => {
  const { target, toggle, targetProps, ...tooltipProps } = usePopper<HTMLButtonElement>({
    placement: 'bottom-end',
  })
  return (
    <div className="input-group">
      <select
        value={values.date_macro}
        className={`form-select ${!isLarge ? 'form-select-sm' : ''}`}
        onChange={(e) => {
          if (!isDateMacro(e.target.value)) {
            return
          }
          if (e.target.value === 'custom') {
            toggle()
          } else {
            const dateMacroQuery = { ...values, date_macro: e.target.value }
            const _interval = getIntervalByDateMacro(dateMacroQuery, program)
            const _steps = filterStepsByDuration(_interval.toDuration())
            handleSelect({
              date_macro: e?.target?.value,
              unit: _steps.find((s) => s === values.unit) || _steps[0],
              start_date: _interval.start?.toJSDate(),
              end_date: _interval.end?.toJSDate(),
              timezone: values.timezone,
            })
          }
        }}
      >
        {new Array<DateMacro>(
          'last7days',
          'last30days',
          'thismonth',
          'thisquarter',
          'thisyear',
          'alltime',
          'custom',
        ).map((option: DateMacro) => {
          const dateMacro: string = formatDateMacro(option)
          return (
            <option key={option} value={option}>
              {dateMacro.charAt(0).toUpperCase() + dateMacro.slice(1)}
            </option>
          )
        })}
      </select>
      <button
        {...targetProps}
        ref={target}
        className="btn btn-sm border"
        style={{ textTransform: 'unset' }}
      >
        <FontAwesomeIcon icon={faCalendar} className="me-2" />
        {formatDateRange(interval)}
      </button>
      <Popover {...tooltipProps} toggle={toggle} style={{ maxWidth: 350 }}>
        <DateMacroRange
          values={interval}
          program={program}
          onSelect={(value: Interval) => {
            const dateMacroQuery = {
              ...values,
              date_macro: 'custom' as DateMacro,
              start_date: value.start?.toJSDate(),
              end_date: value.end?.toJSDate(),
            }
            const _interval = getIntervalByDateMacro(dateMacroQuery, program)
            const _steps = filterStepsByDuration(_interval.toDuration())
            handleSelect({
              ...values,
              date_macro: 'custom',
              start_date: _interval.start?.toJSDate(),
              end_date: _interval.end?.toJSDate(),
              unit: _steps.find((s) => s === values.unit) || _steps[0],
            })
          }}
        />
      </Popover>
    </div>
  )
}
